import React from "react";
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, LinearProgress, Stack, Typography } from "@mui/material";
import { ownerApi } from "../../../../api/owner";
import { formatApiError } from "../../../../api/client";
import { compactActionSx, money } from "./ownerCenterUi";

function dateLabel(value) {
  return value ? new Intl.DateTimeFormat("es-AR", { day: "2-digit", month: "short", year: "numeric" }).format(new Date(value)) : "—";
}

function statusLabel(row) {
  if (row.dueState === "settled") return "Pagado";
  if (row.dueState === "overdue") return row.paymentStatus === "partial" ? "Vencido · parcial" : "Vencido";
  if (row.paymentStatus === "partial") return "Parcial";
  if (row.dueState === "due") return "Vence hoy";
  return "Pendiente";
}

export default function OwnerRecurringPeriodsDialog({ open, plan, onClose, onRecord }) {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const load = React.useCallback(async () => {
    if (!open || !plan) return;
    setLoading(true); setError("");
    try {
      const response = await ownerApi.listRecurringOccurrences(plan.id || plan._id, { limit: 30 });
      setRows(response?.occurrences || []);
    } catch (err) { setError(formatApiError(err, "No pude cargar los períodos de este acuerdo.")); }
    finally { setLoading(false); }
  }, [open, plan]);
  React.useEffect(() => { load(); }, [load]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle><Typography component="div" sx={{ fontWeight: 1000 }}>Períodos · {plan?.name}</Typography><Typography sx={{ color: "text.secondary", fontSize: ".8rem", fontWeight: 700 }}>Esperado, dinero real aplicado y saldo de cada vencimiento.</Typography></DialogTitle>
      {loading ? <LinearProgress /> : null}
      <DialogContent dividers sx={{ p: { xs: 1.2, sm: 2 } }}>
        {error ? <Alert severity="error" action={<Button onClick={load}>Reintentar</Button>}>{error}</Alert> : null}
        {!loading && !error && !rows.length ? <Alert severity="info">Todavía no hay pagos reales. El próximo vencimiento se mantiene como expectativa, no como movimiento.</Alert> : null}
        <Stack divider={<Divider flexItem />}>
          {rows.map((row) => (
            <Box key={row.periodKey} sx={{ py: 1.05, display: { xs: "block", sm: "grid" }, gridTemplateColumns: "1.25fr 1fr 1fr 1fr 1fr auto", gap: 1, alignItems: "center" }}>
              <Box><Typography sx={{ fontWeight: 950, fontSize: ".82rem" }}>{dateLabel(row.dueAt)}</Typography><Typography sx={{ color: "text.secondary", fontSize: ".66rem" }}>{row.periodKey}</Typography></Box>
              <Typography sx={{ fontWeight: 850, fontSize: ".78rem" }}>Esperado {money(row.expectedAmount, row.currency)}</Typography>
              <Typography sx={{ fontWeight: 850, fontSize: ".78rem" }}>{plan?.direction === "income" ? "Cobrado" : "Pagado"} {money(row.paidAmount, row.currency)}</Typography>
              <Typography sx={{ fontWeight: 1000, fontSize: ".78rem" }}>Pendiente {money(row.pendingAmount, row.currency)}</Typography>
              <Stack spacing={.35}><Chip size="small" label={statusLabel(row)} color={row.dueState === "overdue" ? "warning" : row.paymentStatus === "paid" ? "success" : "default"} sx={{ width: "fit-content", fontWeight: 900 }} /><Typography sx={{ color: "text.secondary", fontSize: ".65rem" }}>{row.movementCount || 0} movimiento{row.movementCount === 1 ? "" : "s"}</Typography></Stack>
              {row.pendingAmount > .005 ? <Button size="small" variant={row.dueState === "overdue" ? "contained" : "outlined"} onClick={() => onRecord?.(row)} sx={compactActionSx}>{row.paidAmount > .005 ? "Registrar otro" : "Registrar"}</Button> : null}
              {row.payments?.length ? <Box sx={{ gridColumn: { sm: "1 / -1" }, mt: .45, pl: { sm: 1 }, borderLeft: { sm: "2px solid", borderColor: "divider" } }}>{row.payments.map((payment) => <Typography key={payment.id} sx={{ color: "text.secondary", fontSize: ".68rem", py: .12 }}>{dateLabel(payment.occurredAt)} · {money(payment.amount, row.currency)}{payment.account?.name ? ` · ${payment.account.name}` : ""}{payment.sourceKind ? ` · ${payment.sourceKind}` : ""}</Typography>)}</Box> : null}
            </Box>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions><Button onClick={onClose} sx={compactActionSx}>Cerrar</Button></DialogActions>
    </Dialog>
  );
}
